import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { GLOBAL } from 'src/app/_config/config';
import { BusinessName, CatalogAdmin, CatalogPost, CatalogPostResponse, DataCatalog } from 'src/app/interfaces/catalogs.interfaces';
import { deleteResponse } from 'src/app/interfaces/exportationBatches.interfaces';
import { Customer } from 'src/app/interfaces/lotes.interfaces';

/**
 * Servicio que administra los catálogos del sistema y sus elementos, así como
 * los clientes y razones sociales asociados, permitiendo consultarlos, crearlos,
 * actualizarlos y eliminarlos mediante los endpoints REST del backend.
 */
@Injectable({
  providedIn: 'root'
})
export class CatalogsService {
  listTable: any[] = [];
  listTableFilter: any[] = [];

  constructor(
    private http: HttpClient
  ) { }

  /**
   * Obtiene la lista de tipos de catálogo disponibles.
   * @returns Observable con la lista de tipos de catálogo.
   */
  getListCatalogTypes(): Observable<CatalogAdmin[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.list}`;
    return this.http.get<CatalogAdmin[]>(url);
  }

  /**
   * Obtiene los elementos de un catálogo específico.
   * @param id - Identificador del catálogo.
   * @returns Observable con la lista de elementos del catálogo.
   */
  getListItems(id: number): Observable<any[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.items}${id}`;
    return this.http.get<any[]>(url);
  }

  /**
   * Obtiene los elementos de un catálogo que dependen de otro elemento.
   * @param idCatalog - Identificador del catálogo.
   * @param id - Identificador del elemento del que depende.
   * @returns Observable con la lista de elementos dependientes.
   */
  getListItemsWithDependence(idCatalog: number, id: number): Observable<any[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.items}${idCatalog}/${id}`;
    return this.http.get<any[]>(url);
  }

  /**
   * Crea un nuevo elemento de catálogo.
   * @param item - Datos del elemento a crear.
   * @returns Observable con la respuesta de la creación.
   */
  createItem(item: CatalogPost): Observable<CatalogPostResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.createItem}`;
    return this.http.post<CatalogPostResponse>(url, item);
  }

  /**
   * Actualiza un elemento de catálogo existente.
   * @param item - Datos del elemento a actualizar.
   * @returns Observable con la respuesta de la actualización.
   */
  updateItem(item: CatalogPost): Observable<deleteResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.updateItem}`;
    return this.http.put<deleteResponse>(url, item);
  }

  // Customer
  /**
   * Obtiene la lista de clientes filtrados por muelles.
   * @returns Observable con la lista de clientes.
   */
  getCustomerByMuelles(): Observable<Customer[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.customer.listByMuelles}`;
    return this.http.get<Customer[]>(url);
  }

  /**
   * Crea un nuevo cliente.
   * @param id - Identificador del cliente.
   * @param name - Nombre del cliente.
   * @returns Observable con el identificador del cliente creado.
   */
  createCustomer(id: number, name: string): Observable<{id: number}> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.customer.create}`;
    return this.http.post<{id: number}>(url, { id, name });
  }

  /**
   * Elimina un cliente.
   * @param id - Identificador del cliente a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteCustomer(id: number): Observable<deleteResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.customer.delete}`;
    return this.http.delete<deleteResponse>(url, {
      headers: new HttpHeaders(),
      body: { id }
    });
  }

  // Business Name
  /**
   * Obtiene las razones sociales asociadas a un cliente.
   * @param id - Identificador del cliente.
   * @returns Observable con la lista de razones sociales.
   */
  getBusinessName(id: number): Observable<BusinessName[]> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.bussinessName.get}${id}`;
    return this.http.get<BusinessName[]>(url);
  }

  /**
   * Asocia una razón social a un cliente.
   * @param id_customer - Identificador del cliente.
   * @param id_business_name - Identificador de la razón social.
   * @returns Observable con la respuesta de la operación.
   */
  createBusinessName(id_customer: number, id_business_name: number): Observable<deleteResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.bussinessName.create}`;
    return this.http.post<deleteResponse>(url, { id_customer, id_business_name });
  }

  /**
   * Elimina la asociación entre una razón social y un cliente.
   * @param id_customer - Identificador del cliente.
   * @param id_business_name - Identificador de la razón social.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteBusinessName(id_customer: number, id_business_name: number): Observable<deleteResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.bussinessName.delete}`;
    return this.http.delete<deleteResponse>(url, {
      headers: new HttpHeaders(),
      body: {
        id_customer,
        id_business_name
      }
    });
  }

  /**
   * Elimina un elemento de catálogo.
   * @param item - Datos del elemento a eliminar.
   * @returns Observable con la respuesta de la eliminación.
   */
  deleteItem(item: CatalogPost): Observable<deleteResponse> {
    const url: string = `${GLOBAL.url}${GLOBAL.catalog.deleteItem}`;
    return this.http.delete<deleteResponse>(url, {
      headers: new HttpHeaders(),
      body: item
    });
  }
}
